import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, Loader2 } from "lucide-react";
import { API_BASE, setSession, roleHome } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const GoogleG = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

/** "Continue with Google" button for the login page (requires internet). */
export function GoogleSignInButton() {
  const { t } = useT();
  const start = () => {
    if (!navigator.onLine) {
      toast.error(t("auth.googleNeedsInternet"));
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/subscriber";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="mt-5">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t("auth.googleOr")}</span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <button
        type="button"
        data-testid="auth-google-btn"
        onClick={start}
        className="w-full py-3.5 rounded-full border-2 border-border bg-card hover:bg-muted transition-colors flex items-center justify-center gap-3 font-semibold text-base"
      >
        <GoogleG /> {t("auth.googleBtn")}
      </button>
      <p className="text-xs text-muted-foreground text-center mt-2" data-testid="auth-google-hint">
        {t("auth.googleHint")}
      </p>
    </div>
  );
}

/** Rendered by App when the URL fragment carries #session_id= (return from Emergent Auth). */
export function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { t } = useT();
  const hasProcessed = useRef(false); // one-time exchange guard (StrictMode-safe)
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    // Clean the fragment immediately so the session_id never lingers in history.
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }
    axios
      .post(`${API_BASE}/auth/google-session`, { session_id: sessionId }, { withCredentials: true })
      .then((r) => {
        setSession(r.data.token, r.data.user);
        toast.success(t("msg.welcome", { name: r.data.user.name }));
        nav(roleHome(r.data.user.role), { replace: true });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || t("auth.googleFailed"));
      });
  }, [location.hash, nav, t]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="w-full max-w-md text-center space-y-5" data-testid="google-callback-error">
          <div className="w-16 h-16 mx-auto rounded-3xl bg-destructive/10 grid place-items-center">
            <ShieldAlert className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="font-display text-2xl font-extrabold">{t("auth.googleFailed")}</h1>
          <p className="text-muted-foreground leading-relaxed">{error}</p>
          <Button asChild className="am-btn-primary w-full py-6 text-base">
            <Link to="/login" data-testid="google-callback-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t("auth.backToSignin")}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="google-callback-loading">
      <div className="flex flex-col items-center gap-4 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm font-semibold">{t("auth.googleSigningIn")}</p>
      </div>
    </div>
  );
}
